// src/components/dashboard/AdminCalendar.js
import React, { useState, useEffect } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import frLocale from "@fullcalendar/core/locales/fr";
import { useTheme } from "../../context/ThemeContext";
import { useAuth } from "../../context/AuthContext";
import { db } from "../../firebase/config";
import { collection, getDocs } from "firebase/firestore";
import { 
  Plus, 
  Download, 
  Filter, 
  Search, 
  Calendar as CalendarIcon, 
  Users, 
  TrendingUp,
  Eye,
  EyeOff,
  Palette,
  Clock,
  CheckCircle2,
  AlertCircle,
  Pause,
  Play
} from "lucide-react";
import { Link } from "react-router-dom";
import AdminEventDetails from "./AdminEventDetails";

// Couleurs par rôle admin
const ADMIN_COLORS = {
  'Super Admin': '#8B5CF6',
  'superadmin': '#8B5CF6',
  'Chef de Projet': '#10B981', 
  'projectmanager': '#10B981',
  'Chargé d\'Affaire': '#F59E0B',
  'charge-affaire': '#F59E0B',
  'Admin': '#3B82F6',
  'admin': '#3B82F6'
};

// Styles des statuts
const STATUS_STYLES = {
  'a-programmer': { color: '#EAB308', label: 'À programmer', icon: '⏳' },
  'planifie': { color: '#3B82F6', label: 'Planifié', icon: '📅' },
  'en-cours': { color: '#F97316', label: 'En cours', icon: '🔄' },
  'termine': { color: '#10B981', label: 'Terminé', icon: '✅' },
  'reporte': { color: '#EF4444', label: 'Reporté', icon: '⏸️' }
};

// Types d'événements
const EVENT_TYPES = {
  'visite-technique': { label: 'Visite technique', icon: '🔍' },
  'installation': { label: 'Installation', icon: '🔧' },
  'mise-en-service': { label: 'Mise en service', icon: '⚡' },
  'maintenance': { label: 'Maintenance', icon: '🛠️' },
  'rdv-client': { label: 'Rendez-vous', icon: '🤝' },
  'controle': { label: 'Contrôle', icon: '✅' },
  'autre': { label: 'Autre', icon: '📋' }
};

const AdminCalendar = () => {
  const { user } = useAuth();
  const themeContext = useTheme();
  const theme = themeContext?.theme || 'light';
  
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [admins, setAdmins] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  
  // Filtres
  const [filters, setFilters] = useState({
    admin: 'all',
    status: 'all',
    type: 'all',
    showColors: true
  });

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    aProgrammer: 0,
    planifies: 0,
    termines: 0,
    parAdmin: {}
  });

  console.log("📅 AdminCalendar chargé");

  // Chargement des données
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Charger les admins
        const usersSnapshot = await getDocs(collection(db, "users"));
        const adminsData = {};
        usersSnapshot.docs.forEach(doc => {
          const userData = doc.data();
          adminsData[doc.id] = {
            name: userData.displayName || userData.email,
            role: userData.role,
            email: userData.email,
            color: ADMIN_COLORS[userData.role] || '#6B7280'
          };
        });
        setAdmins(adminsData);

        // Charger les événements
        const eventsSnapshot = await getDocs(collection(db, "events"));
        const eventsData = eventsSnapshot.docs.map(doc => {
          const data = doc.data();
          const responsible = adminsData[data.responsableId];
          
          return {
            id: doc.id,
            title: data.title,
            start: data.start?.toDate ? data.start.toDate() : new Date(data.start),
            end: data.end?.toDate ? data.end.toDate() : new Date(data.end),
            backgroundColor: responsible?.color || data.color || '#6B7280',
            borderColor: responsible?.color || data.color || '#6B7280',
            textColor: '#FFFFFF',
            extendedProps: {
              ...data,
              responsableName: responsible?.name || 'Non assigné',
              responsableRole: responsible?.role || '',
              statusLabel: STATUS_STYLES[data.status]?.label || data.status,
              statusIcon: STATUS_STYLES[data.status]?.icon || '📋',
              typeLabel: EVENT_TYPES[data.eventType]?.label || data.eventType,
              typeIcon: EVENT_TYPES[data.eventType]?.icon || '📋'
            }
          };
        });

        console.log("📅 Événements chargés:", eventsData.length);
        setEvents(eventsData);
        calculateStats(eventsData);
      } catch (error) {
        console.error("Erreur lors du chargement:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Calcul des statistiques
  const calculateStats = (eventsData) => {
    const statsData = {
      total: eventsData.length,
      aProgrammer: eventsData.filter(e => e.extendedProps.status === 'a-programmer').length,
      planifies: eventsData.filter(e => e.extendedProps.status === 'planifie').length,
      termines: eventsData.filter(e => e.extendedProps.status === 'termine').length,
      parAdmin: {}
    };

    // Compter par admin
    eventsData.forEach(event => {
      const adminId = event.extendedProps.responsableId;
      if (adminId && admins[adminId]) {
        const adminName = admins[adminId].name;
        statsData.parAdmin[adminName] = (statsData.parAdmin[adminName] || 0) + 1;
      }
    });

    setStats(statsData);
  };

  // Filtrage des événements
  useEffect(() => {
    let filtered = [...events];

    // Filtre par recherche
    if (searchTerm) {
      filtered = filtered.filter(event => 
        event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.extendedProps.clientName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.extendedProps.responsableName.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtre par admin
    if (filters.admin !== 'all') {
      filtered = filtered.filter(event => event.extendedProps.responsableId === filters.admin);
    }

    // Filtre par statut
    if (filters.status !== 'all') {
      filtered = filtered.filter(event => event.extendedProps.status === filters.status);
    }

    // Filtre par type
    if (filters.type !== 'all') {
      filtered = filtered.filter(event => event.extendedProps.eventType === filters.type);
    }

    setFilteredEvents(filtered);
  }, [events, searchTerm, filters]);

  // Gestionnaire de clic sur événement
  const handleEventClick = ({ event }) => {
    const fullEvent = events.find(e => e.id === event.id);
    setSelectedEvent(fullEvent);
  };

  // Gestionnaire de clic sur date (comme Google Calendar)
  const handleDateClick = (selectInfo) => {
    const selectedDate = selectInfo.dateStr;
    console.log("📅 Clic sur date:", selectedDate);
    
    // Naviguer vers le formulaire avec la date pré-remplie
    const url = `/admin/calendar/add?date=${selectedDate}`;
    window.location.href = url;
  };

  // Gestionnaire de mise à jour d'événement
  const handleEventUpdate = (updatedEvent, isDeleted = false) => {
    if (isDeleted) {
      setEvents(events.filter(e => e.id !== selectedEvent.id));
      setSelectedEvent(null);
      return;
    }
    
    if (updatedEvent) {
      setEvents(events.map(e => e.id === updatedEvent.id ? updatedEvent : e));
    }
  };

  // Export Google Calendar
  const handleExport = () => {
    const formatDate = (date) => {
      return date.toISOString().replace(/[:\-]|\.\d{3}/g, '');
    };

    const icsEvents = filteredEvents.map(event => {
      return [
        'BEGIN:VEVENT',
        `UID:${event.id}@energesync.com`,
        `DTSTAMP:${formatDate(new Date())}`,
        `DTSTART:${formatDate(event.start)}`,
        `DTEND:${formatDate(event.end)}`,
        `SUMMARY:${event.title}`,
        `DESCRIPTION:Client: ${event.extendedProps.clientName}\\nResponsable: ${event.extendedProps.responsableName}\\nStatut: ${event.extendedProps.statusLabel}`,
        event.extendedProps.location ? `LOCATION:${event.extendedProps.location}` : '',
        'END:VEVENT'
      ].filter(line => line !== '').join('\r\n');
    }).join('\r\n');

    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//EnergeSync//Admin Calendar//FR',
      icsEvents,
      'END:VCALENDAR'
    ].join('\r\n');

    const blob = new Blob([icsContent], { type: 'text/calendar' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'agenda_admin_energesync.ics';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'
      }`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className={theme === 'dark' ? 'text-white' : 'text-gray-600'}>
            Chargement de l'agenda...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen transition-all duration-300 ${
      theme === 'dark' 
        ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900' 
        : 'bg-gradient-to-br from-blue-50 via-white to-purple-50'
    }`}>
      <div className="max-w-7xl mx-auto p-6">
        {/* Header magnifique */}
        <div className={`rounded-2xl p-8 mb-8 shadow-xl transition-all duration-300 hover:shadow-2xl ${
          theme === 'dark' ? 'bg-gray-800/50 backdrop-blur border border-gray-700' : 'bg-white/70 backdrop-blur border border-white'
        }`}>
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
              <div className="p-3 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg">
                <CalendarIcon className="w-8 h-8" />
              </div>
              <div>
                <h1 className={`text-4xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  📅 Agenda Administrateur
                </h1>
                <p className={`text-lg ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                  Gérez vos interventions et plannings
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center space-x-4">
              <button
                onClick={handleExport}
                disabled={filteredEvents.length === 0}
                className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-medium transition-all duration-200 hover:scale-105 disabled:opacity-50 ${
                  theme === 'dark'
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : 'bg-green-500 hover:bg-green-600 text-white'
                } shadow-lg hover:shadow-xl`}
              >
                <Download className="w-5 h-5" />
                <span>Exporter</span>
              </button>

              <Link
                to="/admin/calendar/add"
                className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-medium transition-all duration-200 hover:scale-105 ${
                  theme === 'dark'
                    ? 'bg-blue-600 hover:bg-blue-700 text-white'
                    : 'bg-blue-500 hover:bg-blue-600 text-white'
                } shadow-lg hover:shadow-xl`}
              >
                <Plus className="w-5 h-5" />
                <span>Nouvel événement</span>
              </Link>
            </div>
          </div>

          {/* Statistiques */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className={`p-4 rounded-xl ${theme === 'dark' ? 'bg-gray-700/50' : 'bg-blue-50'} border ${theme === 'dark' ? 'border-gray-600' : 'border-blue-200'}`}>
              <div className="flex items-center space-x-3">
                <CalendarIcon className="w-8 h-8 text-blue-600" />
                <div>
                  <p className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    {stats.total}
                  </p>
                  <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    Total événements
                  </p>
                </div>
              </div>
            </div>

            <div className={`p-4 rounded-xl ${theme === 'dark' ? 'bg-gray-700/50' : 'bg-yellow-50'} border ${theme === 'dark' ? 'border-gray-600' : 'border-yellow-200'}`}>
              <div className="flex items-center space-x-3">
                <Clock className="w-8 h-8 text-yellow-600" />
                <div>
                  <p className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    {stats.aProgrammer}
                  </p>
                  <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    À programmer
                  </p>
                </div>
              </div>
            </div>

            <div className={`p-4 rounded-xl ${theme === 'dark' ? 'bg-gray-700/50' : 'bg-blue-50'} border ${theme === 'dark' ? 'border-gray-600' : 'border-blue-200'}`}>
              <div className="flex items-center space-x-3">
                <Play className="w-8 h-8 text-blue-600" />
                <div>
                  <p className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    {stats.planifies}
                  </p>
                  <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    Planifiés
                  </p>
                </div>
              </div>
            </div>

            <div className={`p-4 rounded-xl ${theme === 'dark' ? 'bg-gray-700/50' : 'bg-green-50'} border ${theme === 'dark' ? 'border-gray-600' : 'border-green-200'}`}>
              <div className="flex items-center space-x-3">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
                <div>
                  <p className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    {stats.termines}
                  </p>
                  <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    Terminés
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filtres et recherche */}
        <div className={`rounded-xl p-6 mb-6 shadow-lg ${
          theme === 'dark' ? 'bg-gray-800/50 backdrop-blur border border-gray-700' : 'bg-white/70 backdrop-blur border border-white'
        }`}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Recherche */}
            <div className="relative">
              <Search className={`absolute left-3 top-3 w-5 h-5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
              <input
                type="text"
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`w-full pl-10 pr-4 py-2 rounded-lg border transition-colors ${
                  theme === 'dark' 
                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              />
            </div>

            {/* Filtre Admin */}
            <select
              value={filters.admin}
              onChange={(e) => setFilters(prev => ({ ...prev, admin: e.target.value }))}
              className={`px-4 py-2 rounded-lg border transition-colors ${
                theme === 'dark' 
                  ? 'bg-gray-700 border-gray-600 text-white' 
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
            >
              <option value="all">Tous les admins</option>
              {Object.entries(admins).map(([id, admin]) => (
                <option key={id} value={id}>{admin.name}</option>
              ))}
            </select>

            {/* Filtre Statut */}
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              className={`px-4 py-2 rounded-lg border transition-colors ${
                theme === 'dark' 
                  ? 'bg-gray-700 border-gray-600 text-white' 
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
            >
              <option value="all">Tous les statuts</option>
              {Object.entries(STATUS_STYLES).map(([key, status]) => (
                <option key={key} value={key}>{status.icon} {status.label}</option>
              ))}
            </select>

            {/* Filtre Type */}
            <select
              value={filters.type}
              onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
              className={`px-4 py-2 rounded-lg border transition-colors ${
                theme === 'dark' 
                  ? 'bg-gray-700 border-gray-600 text-white' 
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
            >
              <option value="all">Tous les types</option>
              {Object.entries(EVENT_TYPES).map(([key, type]) => (
                <option key={key} value={key}>{type.icon} {type.label}</option>
              ))}
            </select>

            {/* Toggle couleurs */}
            <button
              onClick={() => setFilters(prev => ({ ...prev, showColors: !prev.showColors }))}
              className={`flex items-center justify-center space-x-2 px-4 py-2 rounded-lg transition-all ${
                filters.showColors
                  ? theme === 'dark'
                    ? 'bg-purple-600 text-white'
                    : 'bg-purple-500 text-white'
                  : theme === 'dark'
                    ? 'bg-gray-700 text-gray-300'
                    : 'bg-gray-100 text-gray-700'
              }`}
            >
              <Palette className="w-5 h-5" />
              <span>Couleurs</span>
            </button>
          </div>

          {/* Résultats */}
          <div className="mt-4 flex items-center justify-between">
            <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              {filteredEvents.length} événement(s) affiché(s) sur {events.length}
            </p>
            
            {/* Légende des couleurs */}
            {filters.showColors && (
              <div className="flex items-center space-x-4">
                <span className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  Légende :
                </span>
                {Object.entries(admins).slice(0, 4).map(([id, admin]) => (
                  <div key={id} className="flex items-center space-x-2">
                    <div 
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: admin.color }}
                    ></div>
                    <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      {admin.role}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Calendrier */}
        <div className={`rounded-xl p-6 shadow-xl ${
          theme === 'dark' ? 'bg-gray-800/50 backdrop-blur border border-gray-700' : 'bg-white/70 backdrop-blur border border-white'
        }`}>
          <div className="admin-calendar-container">
            <FullCalendar
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              initialView="dayGridMonth"
              headerToolbar={{
                left: "prev,next today",
                center: "title",
                right: "dayGridMonth,timeGridWeek,timeGridDay"
              }}
              locale="fr"
              locales={[frLocale]}
              events={filteredEvents}
              eventClick={handleEventClick}
              dateClick={handleDateClick}
              selectable={true}
              selectMirror={true}
              height="70vh"
              eventDisplay="block"
              eventBackgroundColor="transparent"
              eventBorderColor="transparent"
              eventTextColor="#FFFFFF"
              eventContent={(eventInfo) => {
                const event = eventInfo.event;
                const props = event.extendedProps;
                return (
                  <div className="p-1 rounded text-xs font-medium overflow-hidden">
                    <div className="truncate">
                      {props.typeIcon} {event.title}
                    </div>
                    <div className="truncate text-xs opacity-90">
                      👤 {props.clientName || 'Client'}
                    </div>
                    <div className="truncate text-xs opacity-75">
                      {props.statusIcon} {props.statusLabel}
                    </div>
                  </div>
                );
              }}
            />
          </div>
        </div>

        {/* Modal de détails */}
        {selectedEvent && (
          <AdminEventDetails 
            event={selectedEvent} 
            onClose={() => setSelectedEvent(null)} 
            onUpdate={handleEventUpdate}
          />
        )}
      </div>

      {/* Styles CSS pour FullCalendar */}
      <style jsx global>{`
        .fc {
          font-family: system-ui, -apple-system, sans-serif;
        }
        
        .fc-event {
          border-radius: 6px !important;
          margin: 1px 0 !important;
          font-size: 11px !important;
          font-weight: 500 !important;
        }
        
        .fc-daygrid-event {
          border-radius: 4px !important;
          margin: 2px 1px !important;
        }
        
        .fc-toolbar-title {
          font-size: 1.5rem !important;
          font-weight: 700 !important;
          color: ${theme === 'dark' ? '#F9FAFB' : '#111827'} !important;
        }
        
        .fc-button {
          background: ${theme === 'dark' ? '#374151' : '#F3F4F6'} !important;
          border: 1px solid ${theme === 'dark' ? '#4B5563' : '#D1D5DB'} !important;
          color: ${theme === 'dark' ? '#F9FAFB' : '#374151'} !important;
          border-radius: 8px !important;
          font-weight: 500 !important;
          transition: all 0.2s !important;
        }
        
        .fc-button:hover {
          background: ${theme === 'dark' ? '#4B5563' : '#E5E7EB'} !important;
          transform: translateY(-1px) !important;
        }
        
        .fc-button-active {
          background: #3B82F6 !important;
          border-color: #2563EB !important;
          color: white !important;
        }
        
        .fc-today-button {
          background: #10B981 !important;
          border-color: #059669 !important;
          color: white !important;
        }
        
        .fc-daygrid-day {
          background: ${theme === 'dark' ? 'transparent' : 'transparent'} !important;
        }
        
        .fc-day-today {
          background: ${theme === 'dark' ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.05)'} !important;
        }
        
        .fc-day-header {
          background: ${theme === 'dark' ? '#374151' : '#F9FAFB'} !important;
          color: ${theme === 'dark' ? '#F9FAFB' : '#374151'} !important;
          font-weight: 600 !important;
          padding: 8px !important;
          border: none !important;
        }
        
        .fc-daygrid-day-number {
          color: ${theme === 'dark' ? '#F9FAFB' : '#374151'} !important;
          font-weight: 500 !important;
          text-decoration: none !important;
        }
        
        .fc-scrollgrid {
          border: 1px solid ${theme === 'dark' ? '#374151' : '#E5E7EB'} !important;
          border-radius: 12px !important;
          overflow: hidden !important;
        }
        
        .fc-scrollgrid-sync-table {
          border: none !important;
        }
        
        td.fc-daygrid-day {
          border: 1px solid ${theme === 'dark' ? '#374151' : '#F3F4F6'} !important;
        }
        
        .fc-more-link {
          color: #3B82F6 !important;
          font-weight: 600 !important;
        }
      `}</style>
    </div>
  );
};

export default AdminCalendar;